import React, { useState, useEffect, useMemo } from "react";
import { getFactBasic } from "../../../../api/factApi";
import { getSketchDesignEstimate } from "../../../../api/projectEstimatesApi";
import Preloader from "../../../Preloader/Preloader";
import "./Fact.css";

const formatMoney = (value) => {
  if (value == null || value === "" || Number.isNaN(Number(value)))
    return "–";
  return Number(value).toLocaleString("ru-RU", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

const formatPercent = (value) => {
  if (value == null || value === "" || Number.isNaN(Number(value)))
    return "–";
  return `${Number(value).toFixed(2)}`;
};

/**
 * Нормализует ответ GET /sketch_design/fact/calculate/{project_id} (массив)
 * в формат { work_types_statistics, final_spending } для компонента.
 */
function normalizeFactCalculateResponse(arr) {
  if (!Array.isArray(arr)) return { work_types_statistics: [], final_spending: 0 };
  const work_types_statistics = arr.map((item) => {
    const corrections = item.corrections || {};
    const byMonth = {};
    let total = 0;
    Object.entries(corrections).forEach(([ym, list]) => {
      const sum = (list || []).reduce(
        (s, c) => s + (Number(c.completion_sum_after_correction) || 0),
        0
      );
      byMonth[ym] = sum;
      total += sum;
    });
    return {
      work_type: item.work_type,
      work_type_id: item.work_type?.id,
      work_type_name: item.work_type?.name,
      corrections: item.corrections,
      twenty_percents: item.twenty_percents,
      full_work_type_reports_sum: total,
      final_sum: item.final_sum ?? item.work_type?.final_sum, // сумма выделенная на вид работ
      byMonth,
      total,
    };
  });
  const final_spending = work_types_statistics.reduce(
    (s, wt) => s + (wt.full_work_type_reports_sum || 0),
    0
  );
  return { work_types_statistics, final_spending };
}

/** Получить уникальные месяцы из отчётов или из corrections в формате { key: "2025-10", label: "Октябрь 2025" } */
function getPeriodsFromReports(workTypesStats) {
  const monthSet = new Set();
  (workTypesStats || []).forEach((wt) => {
    if (wt.byMonth && typeof wt.byMonth === "object") {
      Object.keys(wt.byMonth).forEach((ym) => monthSet.add(ym));
    } else {
      (wt.reports || []).forEach((r) => {
        if (r.datetime) {
          const [y, m] = r.datetime.slice(0, 7).split("-");
          monthSet.add(`${y}-${m}`);
        }
      });
    }
  });
  const months = Array.from(monthSet).sort();
  return months.map((ym) => {
    const [y, m] = ym.split("-");
    const names = [
      "янв.", "февр.", "марта", "апр.", "мая", "июня", "июля", "авг.", "сент.", "окт.", "нояб.", "дек.",
    ];
    const label = `${names[parseInt(m, 10) - 1]} ${y}`;
    return { key: ym, label };
  });
}

/** Сумма по секундам и ставке (тенге за час) */
function costFromSeconds(seconds, hourRate) {
  if (seconds == null || hourRate == null) return 0;
  return (Number(seconds) / 3600) * Number(hourRate);
}

/** По work_type и отчётам или по byMonth посчитать сумму по месяцам: { total, "2025-10": sum, ... } */
function getSumsByPeriod(workTypeStat) {
  if (workTypeStat?.byMonth && typeof workTypeStat.byMonth === "object") {
    const byMonth = workTypeStat.byMonth;
    const total = workTypeStat.total ?? Object.values(byMonth).reduce((s, v) => s + Number(v), 0);
    return { total, byMonth };
  }
  const workType = workTypeStat?.work_type || {};
  const hourRate = workType.hour_rate ?? 0;
  const reports = workTypeStat?.reports || [];
  const byMonth = {};
  let total = 0;
  reports.forEach((r) => {
    const cost = costFromSeconds(r.seconds_spend, hourRate);
    total += cost;
    if (r.datetime) {
      const ym = r.datetime.slice(0, 7);
      byMonth[ym] = (byMonth[ym] || 0) + cost;
    }
  });
  return { total, byMonth };
}

const Fact = ({ projectId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [factData, setFactData] = useState(null);
  const [estimateData, setEstimateData] = useState(null);
  const [incomeCollapsed, setIncomeCollapsed] = useState(false);
  const [expenseCollapsed, setExpenseCollapsed] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState("all"); // "all" | "2025-10" | ...
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailWorkType, setDetailWorkType] = useState(null);
  const [detailPeriod, setDetailPeriod] = useState(null);
  const [detailData, setDetailData] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      if (!projectId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const [factRaw, estimate] = await Promise.all([
          getFactBasic(projectId),
          getSketchDesignEstimate(projectId).catch(() => null),
        ]);
        const fact = Array.isArray(factRaw)
          ? normalizeFactCalculateResponse(factRaw)
          : factRaw;
        setFactData(fact);
        setEstimateData(estimate);
      } catch (err) {
        setError(err?.message || "Не удалось загрузить данные факта");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [projectId]);

  const periods = useMemo(
    () => getPeriodsFromReports(factData?.work_types_statistics || []),
    [factData]
  );

  const incomeTotal =
    estimateData?.contract_amount_total ?? factData?.work_types_statistics?.reduce(
      (s, w) => s + (Number(w.final_sum ?? w.work_type?.final_sum) || 0),
      0
    ) ?? 0;

  const expenseTotal = factData?.final_spending ?? 0;
  const workTypesStats = factData?.work_types_statistics || [];
  const expenseSumByWorkType = workTypesStats.reduce(
    (acc, wt) => acc + (Number(wt.full_work_type_reports_sum) || 0),
    0
  );
  const marginalTotal = incomeTotal - expenseSumByWorkType;
  const marginalPercent =
    incomeTotal > 0 ? ((marginalTotal / incomeTotal) * 100).toFixed(2) : "0";

  /** Собрать список записей детализации из corrections (данные уже в ответе calculate) */
  const getDetailRowsFromCorrections = (workTypeStat, periodKey) => {
    const corrections = workTypeStat?.corrections || {};
    const monthNames = [
      "янв.", "февр.", "марта", "апр.", "мая", "июня", "июля", "авг.", "сент.", "окт.", "нояб.", "дек.",
    ];
    const rows = [];
    const periodKeys = periodKey
      ? [periodKey]
      : Object.keys(corrections).sort();
    periodKeys.forEach((ym) => {
      const list = corrections[ym] || [];
      const [y, m] = ym.split("-");
      const periodLabel = `${monthNames[parseInt(m, 10) - 1]} ${y}`;
      list.forEach((c) => {
        rows.push({
          id: c.id,
          period: periodLabel,
          periodKey: ym,
          completion_sum_after_correction: c.completion_sum_after_correction,
          completion_percentage_after_correction: c.completion_percentage_after_correction,
          comment: c.comment,
          user_id: c.user_id,
        });
      });
    });
    return rows;
  };

  const openDetail = (workTypeStat, periodKey) => {
    setDetailWorkType(workTypeStat);
    setDetailPeriod(periodKey);
    setDetailOpen(true);
    const rows = getDetailRowsFromCorrections(workTypeStat, periodKey);
    setDetailData(rows);
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setDetailWorkType(null);
    setDetailPeriod(null);
    setDetailData(null);
  };

  const renderMoney = (value) => (
    <span className="fact-cell-money">
      {value != null && value !== "" && !Number.isNaN(Number(value))
        ? `₸ ${formatMoney(value)}`
        : "–"}
    </span>
  );

  if (!projectId) {
    return (
      <div className="fact-page">
        <div className="fact-empty">Ошибка: ID проекта не передан</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="fact-page">
        <Preloader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="fact-page">
        <div className="fact-empty">Ошибка: {error}</div>
      </div>
    );
  }

  // По макету: всегда все колонки — Всего, месяцы, Остаток, 20% оплаты
  const periodColumns = [
    { key: "all", label: "Всего" },
    ...periods,
    { key: "remaining", label: "Остаток" },
    { key: "twenty_percents", label: "20% оплаты" },
  ];

  // Остаток дохода: договор минус текущий факт расходов (не «закрытая» часть)
  const incomeRemaining = Math.max(0, incomeTotal - expenseSumByWorkType);
  const incomeRowSums = { total: incomeTotal };
  const incomePerMonth = periods.length > 0 ? incomeTotal / periods.length : 0;
  periodColumns.forEach((col) => {
    if (col.key === "all") incomeRowSums[col.key] = incomeTotal;
    else if (col.key === "remaining") incomeRowSums[col.key] = incomeRemaining;
    else if (col.key === "twenty_percents") incomeRowSums[col.key] = incomeTotal * 0.2;
    else incomeRowSums[col.key] = incomePerMonth;
  });

  return (
    <div className="fact-page">
      {/* Карточки */}
      <div className="fact-cards">
        <div className="fact-card">
          <div className="fact-card-title">Доход по проекту</div>
          <div className="fact-card-value">
            ₸ {formatMoney(incomeTotal)}
          </div>
        </div>
        <div className="fact-card">
          <div className="fact-card-title">Факт расходов на дату</div>
          <div className="fact-card-value">
            ₸ {formatMoney(expenseSumByWorkType ?? factData?.final_spending)}
          </div>
        </div>
        <div className="fact-card">
          <div className="fact-card-title">Маржинальный доход</div>
          <div className="fact-card-value positive">
            ₸ {formatMoney(marginalTotal)}
          </div>
          <div className="fact-card-value positive" style={{ fontSize: 14, marginTop: 4 }}>
            {marginalPercent}%
          </div>
        </div>
        <div className="fact-card">
          <div className="fact-card-title">Отклонение от плана</div>
          <div className="fact-card-value muted">–</div>
        </div>
      </div>

      {/* Фильтр по периоду */}
      <div className="fact-period-filter">
        <button
          type="button"
          className={`fact-period-btn ${selectedPeriod === "all" ? "active" : ""}`}
          onClick={() => setSelectedPeriod("all")}
        >
          Всего
        </button>
        {periods.map((p) => (
          <button
            key={p.key}
            type="button"
            className={`fact-period-btn ${selectedPeriod === p.key ? "active" : ""}`}
            onClick={() => setSelectedPeriod(p.key)}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* ДОХОДЫ ПО ПРОЕКТАМ */}
      <div className={`fact-section ${incomeCollapsed ? "collapsed" : ""}`}>
        <div
          className="fact-section-header"
          onClick={() => setIncomeCollapsed(!incomeCollapsed)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === "Enter" && setIncomeCollapsed(!incomeCollapsed)}
        >
          <span>ДОХОДЫ ПО ПРОЕКТАМ</span>
          <span className="section-arrow">⌄</span>
        </div>
        <div className="fact-section-body">
          <div className="fact-table-wrap">
            <table className="fact-table">
              <thead>
                <tr>
                  <th style={{ width: "30%" }}>Вид работ</th>
                  {periodColumns.map((col) => (
                    <th key={col.key} className="fact-cell-money">
                      {col.label}
                      {col.key === "twenty_percents" && (
                        <span
                          className="fact-th-tooltip"
                          title="20% от суммы договора к оплате"
                          aria-label="Подсказка: 20% от суммы договора к оплате"
                        >
                          ?
                        </span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Эскизное проектирование</td>
                  {periodColumns.map((col) => (
                    <td key={col.key} className="fact-cell-total fact-cell-money">
                      {renderMoney(incomeRowSums[col.key])}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* РАСХОДЫ ПО ПРОЕКТАМ */}
      <div className={`fact-section ${expenseCollapsed ? "collapsed" : ""}`}>
        <div
          className="fact-section-header"
          onClick={() => setExpenseCollapsed(!expenseCollapsed)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === "Enter" && setExpenseCollapsed(!expenseCollapsed)}
        >
          <span>РАСХОДЫ ПО ПРОЕКТАМ</span>
          <span className="section-arrow">⌄</span>
        </div>
        <div className="fact-section-body">
          <div className="fact-table-wrap">
            <table className="fact-table">
              <thead>
                <tr>
                  <th style={{ width: "30%" }}>Вид работ</th>
                  {periodColumns.map((col) => (
                    <th key={col.key} className="fact-cell-money">
                      {col.label}
                      {col.key === "twenty_percents" && (
                        <span
                          className="fact-th-tooltip"
                          title="20% от суммы договора к оплате"
                          aria-label="Подсказка: 20% от суммы договора к оплате"
                        >
                          ?
                        </span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {workTypesStats.map((wt) => {
                  const sums = getSumsByPeriod(wt);
                  const fullSum = Number(wt.full_work_type_reports_sum) ?? sums.total; // сумма completion_sum_after_correction за все месяцы
                  const finalSum = Number(wt.final_sum ?? wt.work_type?.final_sum) ?? 0; // всего выделено на вид работ
                  const remaining = finalSum - fullSum; // остаток = Всего − выплаты за все месяцы
                  return (
                    <tr
                      key={wt.work_type_id ?? wt.work_type?.id ?? wt.work_type_name}
                      onClick={() => openDetail(wt, selectedPeriod === "all" ? null : selectedPeriod)}
                    >
                      <td>{wt.work_type_name ?? wt.work_type?.name ?? "–"}</td>
                      {periodColumns.map((col) => {
                        let val;
                        if (col.key === "all") val = finalSum; // Всего = final_sum (сумма выделенная на вид работ)
                        else if (col.key === "remaining") val = remaining;
                        else if (col.key === "twenty_percents") val = wt.twenty_percents ?? 0;
                        else val = sums.byMonth[col.key] ?? 0;
                        return (
                          <td
                            key={col.key}
                            className={col.key === "all" ? "fact-cell-total fact-cell-money" : "fact-cell-money"}
                          >
                            {renderMoney(val)}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ИТОГО */}
      <div className="fact-total-section">
        <table className="fact-table">
          <tbody>
            <tr className="fact-total-row">
              <td style={{ paddingLeft: 16 }}>ИТОГО</td>
              {periodColumns.map((col) => (
                <td key={col.key} className="fact-cell-money" />
              ))}
            </tr>
            <tr className="fact-total-row">
              <td style={{ paddingLeft: 16 }}>ИТОГО расходов</td>
              {periodColumns.map((col) => {
                let expenseVal;
                if (col.key === "all") expenseVal = expenseSumByWorkType;
                else if (col.key === "remaining") {
                  expenseVal = workTypesStats.reduce((s, wt) => {
                    const fs = Number(wt.final_sum ?? wt.work_type?.final_sum) ?? 0;
                    const tot = Number(wt.full_work_type_reports_sum) ?? (getSumsByPeriod(wt).total ?? 0);
                    return s + (fs - tot);
                  }, 0);
                } else if (col.key === "twenty_percents") {
                  expenseVal = workTypesStats.reduce((s, wt) => s + (Number(wt.twenty_percents) ?? 0), 0);
                } else {
                  expenseVal = workTypesStats.reduce(
                    (s, wt) => s + (getSumsByPeriod(wt).byMonth[col.key] ?? 0),
                    0
                  );
                }
                return (
                  <td key={col.key} className="fact-cell-money">
                    {renderMoney(expenseVal)}
                  </td>
                );
              })}
            </tr>
            <tr className="fact-total-row marginal">
              <td style={{ paddingLeft: 16 }}>Маржинальный доход (тг)</td>
              {periodColumns.map((col) => {
                let expenses;
                if (col.key === "all") expenses = expenseSumByWorkType;
                else if (col.key === "remaining") {
                  expenses = workTypesStats.reduce((s, wt) => {
                    const fs = Number(wt.final_sum ?? wt.work_type?.final_sum) ?? 0;
                    const tot = Number(wt.full_work_type_reports_sum) ?? (getSumsByPeriod(wt).total ?? 0);
                    return s + (fs - tot);
                  }, 0);
                } else if (col.key === "twenty_percents") {
                  expenses = workTypesStats.reduce((s, wt) => s + (Number(wt.twenty_percents) ?? 0), 0);
                } else {
                  expenses = workTypesStats.reduce(
                    (s, wt) => s + (getSumsByPeriod(wt).byMonth[col.key] ?? 0),
                    0
                  );
                }
                const incomeCol = col.key === "all" ? incomeTotal : incomeRowSums[col.key];
                const margin = (incomeCol ?? 0) - expenses;
                return (
                  <td key={col.key} className="fact-cell-money">
                    {renderMoney(margin)}
                  </td>
                );
              })}
            </tr>
            <tr className="fact-total-row marginal">
              <td style={{ paddingLeft: 16 }}>Маржинальный доход (%)</td>
              {periodColumns.map((col) => {
                let expenses;
                if (col.key === "all") expenses = expenseSumByWorkType;
                else if (col.key === "remaining") {
                  expenses = workTypesStats.reduce((s, wt) => {
                    const fs = Number(wt.final_sum ?? wt.work_type?.final_sum) ?? 0;
                    const tot = Number(wt.full_work_type_reports_sum) ?? (getSumsByPeriod(wt).total ?? 0);
                    return s + (fs - tot);
                  }, 0);
                } else if (col.key === "twenty_percents") {
                  expenses = workTypesStats.reduce((s, wt) => s + (Number(wt.twenty_percents) ?? 0), 0);
                } else {
                  expenses = workTypesStats.reduce(
                    (s, wt) => s + (getSumsByPeriod(wt).byMonth[col.key] ?? 0),
                    0
                  );
                }
                const incomeCol = col.key === "all" ? incomeTotal : incomeRowSums[col.key];
                const margin = (incomeCol ?? 0) - expenses;
                const pct =
                  (incomeCol ?? 0) > 0
                    ? ((margin / (incomeCol ?? 1)) * 100).toFixed(2)
                    : "0";
                return (
                  <td key={col.key} className="fact-cell-money">
                    % {formatPercent(pct)}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Кнопки */}
      <div className="fact-actions">
        <button type="button" className="fact-btn fact-btn-primary">
          Сохранить
        </button>
        <button type="button" className="fact-btn fact-btn-secondary">
          Печать PDF
        </button>
      </div>

      {/* Панель детализации */}
      {detailOpen && (
        <>
          <div
            className="fact-detail-overlay"
            onClick={closeDetail}
            onKeyDown={(e) => e.key === "Escape" && closeDetail()}
            role="button"
            tabIndex={0}
            aria-label="Закрыть"
          />
          <div className="fact-detail-panel">
            <div className="fact-detail-header">
              <div>
                <div className="fact-detail-title">Детализация:</div>
                <div className="fact-detail-subtitle">
                  {detailWorkType?.work_type_name ?? detailWorkType?.work_type?.name ?? "–"}
                  {detailPeriod && periods.find((p) => p.key === detailPeriod)
                    ? ` / ${periods.find((p) => p.key === detailPeriod).label}`
                    : ""}
                </div>
              </div>
              <button
                type="button"
                className="fact-detail-close"
                onClick={closeDetail}
                aria-label="Закрыть"
              >
                ×
              </button>
            </div>
            <div className="fact-detail-body">
              {detailData && detailData.length > 0 ? (
                <table className="fact-detail-table">
                  <thead>
                    <tr>
                      <th>№</th>
                      <th>Период</th>
                      <th>Сумма выполнения</th>
                      <th>% выполнения</th>
                      <th>Комментарий</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailData.map((row, idx) => (
                      <tr key={row.id ?? idx}>
                        <td>{idx + 1}</td>
                        <td>{row.period ?? "–"}</td>
                        <td>
                          {row.completion_sum_after_correction != null
                            ? `₸ ${formatMoney(row.completion_sum_after_correction)}`
                            : "–"}
                        </td>
                        <td>
                          {row.completion_percentage_after_correction != null
                            ? formatPercent(row.completion_percentage_after_correction)
                            : "–"}
                        </td>
                        <td>{row.comment || "–"}</td>
                      </tr>
                    ))}
                    <tr className="fact-detail-total-row">
                      <td colSpan={2}>ИТОГО</td>
                      <td>
                        {formatMoney(
                          detailData.reduce(
                            (s, r) => s + (Number(r.completion_sum_after_correction) || 0),
                            0
                          )
                        )}{" "}
                        тг
                      </td>
                      <td colSpan={2}>–</td>
                    </tr>
                  </tbody>
                </table>
              ) : (
                <div className="fact-detail-loading">
                  Нет детализации по выбранному виду работ
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Fact;
